/**
 * Per-profile BotBrowser settings: everything the profile editor exposes on
 * top of the `.enc` fingerprint the worker resolves.
 *
 * Every field is optional and unset means "whatever the profile ships with",
 * so an empty object is a valid, complete configuration. The groups mirror the
 * editor's sections. `botbrowser/flags.ts` turns a parsed value into launch
 * flags; accepted values follow BotBrowser's CLI flag reference.
 *
 * Deliberately absent: anything that takes a path on the worker's filesystem
 * or only exists for diagnostics (`--bot-script`, `--proxy-pac-url`,
 * `--bot-profile-dir`, V8Log, CanvasLab/AudioLab recording, Mirror).
 *
 * No transforms: these schemas feed request bodies whose OpenAPI document is
 * generated with `z.toJSONSchema`, which cannot represent them.
 */

import { z } from "zod";

// ============ Limits ============

/** BotBrowser seeds are unsigned 32-bit; 0 means "profile default" there. */
export const SETTINGS_SEED_MAX = 4_294_967_295;

/**
 * Bookmarks and custom headers travel inline as a single argv entry, and Linux
 * caps one argument at 128 KiB (MAX_ARG_STRLEN), so their JSON stays well
 * under that.
 */
export const SETTINGS_INLINE_JSON_MAX_CHARS = 100_000;

export const SETTINGS_BOOKMARKS_MAX_NODES = 2_000;
export const SETTINGS_BOOKMARKS_MAX_DEPTH = 6;
export const SETTINGS_START_URLS_MAX = 20;
export const SETTINGS_CUSTOM_HEADERS_MAX = 50;
export const SETTINGS_PROXY_BYPASS_MAX_CHARS = 512;

// ============ Value validators ============

function isHttpUrl(value: string): boolean {
	try {
		const url = new URL(value);
		return url.protocol === "http:" || url.protocol === "https:";
	} catch {
		return false;
	}
}

function httpUrlSchema(label: string) {
	return z
		.string({ error: `${label} must be text` })
		.trim()
		.min(1, `${label} is required`)
		.max(2048, `${label} must be 2048 characters or less`)
		.refine(isHttpUrl, `${label} must be an http:// or https:// URL`);
}

let timeZoneCache: Set<string> | null | undefined;

function supportedTimeZones(): Set<string> | null {
	if (timeZoneCache !== undefined) return timeZoneCache;
	try {
		timeZoneCache =
			typeof Intl.supportedValuesOf === "function"
				? new Set(Intl.supportedValuesOf("timeZone"))
				: null;
	} catch {
		timeZoneCache = null;
	}
	return timeZoneCache;
}

const IANA_ZONE_PATTERN = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/;

/**
 * An IANA zone name. The supported-values list omits aliases such as
 * `Asia/Calcutta` and, on some engines, `UTC`, so a name the formatter accepts
 * is valid too; the pattern keeps out raw offsets like `+05:00`.
 */
export function isValidTimeZone(value: string): boolean {
	if (!IANA_ZONE_PATTERN.test(value)) return false;
	if (supportedTimeZones()?.has(value)) return true;
	try {
		new Intl.DateTimeFormat("en-US", { timeZone: value });
		return true;
	} catch {
		return false;
	}
}

const BCP47_PATTERN = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{1,8})*$/;

/** A single BCP 47 language tag such as `en-US`. */
export function isValidLocale(value: string): boolean {
	if (!BCP47_PATTERN.test(value)) return false;
	try {
		return Intl.getCanonicalLocales(value).length === 1;
	} catch {
		return false;
	}
}

const TimeZoneSchema = z
	.string({ error: "Enter a timezone" })
	.trim()
	.min(1, "Enter a timezone")
	.refine(isValidTimeZone, "Enter an IANA timezone such as America/New_York");

const LocaleTagSchema = z
	.string({ error: "Enter a locale" })
	.trim()
	.min(1, "Enter a locale")
	.refine(isValidLocale, "Enter a language tag such as en-US or fr-FR");

const VERSION_PATTERN = /^\d{1,4}(?:\.\d{1,5}){0,3}$/;

function versionSchema(label: string, example: string) {
	return z
		.string({ error: `${label} must be text` })
		.trim()
		.regex(VERSION_PATTERN, `${label} must look like ${example}`);
}

function booleanSchema(label: string) {
	return z.boolean({ error: `${label} must be on or off` });
}

function enumSchema<const T extends readonly [string, ...string[]]>(
	values: T,
	label: string,
) {
	return z.enum(values, {
		error: `${label} must be one of: ${values.join(", ")}`,
	});
}

function intSchema(label: string, min: number, max: number) {
	return z
		.number({ error: `${label} must be a number` })
		.int(`${label} must be a whole number`)
		.min(min, `${label} must be at least ${min}`)
		.max(max, `${label} must be at most ${max}`);
}

const SeedSchema = (label: string) => intSchema(label, 1, SETTINGS_SEED_MAX);

/**
 * `{ mode, value }` where `value` only matters, and is then required, when the
 * mode is the custom one. A value kept alongside another mode is ignored, so a
 * form can remember it while the user flips modes.
 */
function modeWithValue<
	const Modes extends readonly [string, ...string[]],
	Value extends z.ZodType,
>(
	modes: Modes,
	customMode: Modes[number],
	value: Value,
	messages: { mode: string; value: string },
) {
	return z
		.object({
			mode: z.enum(modes, { error: messages.mode }),
			value: value.optional(),
		})
		.superRefine((data, ctx) => {
			if (data.mode === customMode && data.value === undefined) {
				ctx.addIssue({
					code: "custom",
					path: ["value"],
					message: messages.value,
				});
			}
		});
}

// ============ Identity ============

export const BROWSER_BRANDS = [
	"chrome",
	"chromium",
	"edge",
	"brave",
	"opera",
	"webview",
] as const;

export const BrowserBrandSchema = enumSchema(BROWSER_BRANDS, "Browser brand");

/** Placeholders BotBrowser substitutes inside `--user-agent`. */
export const USER_AGENT_PLACEHOLDERS = [
	"platform",
	"platform-version",
	"model",
	"ua-full-version",
	"ua-major-version",
	"brand-full-version",
	"architecture",
	"bitness",
] as const;

const USER_AGENT_PLACEHOLDER_SET = new Set<string>(USER_AGENT_PLACEHOLDERS);

// Control characters would end the argv value on some launch paths.
// biome-ignore lint/suspicious/noControlCharactersInRegex: that is the point
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

const UserAgentSchema = z
	.string({ error: "User agent must be text" })
	.trim()
	.min(1, "Enter a user agent or leave it on the profile default")
	.max(512, "User agent must be 512 characters or less")
	.refine(
		(value) => !CONTROL_CHARACTERS.test(value),
		"User agent cannot contain line breaks or control characters",
	)
	.refine(
		(value) =>
			[...value.matchAll(/\{([^{}]*)\}/g)].every((match) =>
				USER_AGENT_PLACEHOLDER_SET.has(match[1] ?? ""),
			),
		`Unknown placeholder. Supported: ${USER_AGENT_PLACEHOLDERS.map((p) => `{${p}}`).join(", ")}`,
	);

export const LocationSchema = z.object({
	lat: z
		.number({ error: "Latitude must be a number" })
		.min(-90, "Latitude must be between -90 and 90")
		.max(90, "Latitude must be between -90 and 90"),
	lng: z
		.number({ error: "Longitude must be a number" })
		.min(-180, "Longitude must be between -180 and 180")
		.max(180, "Longitude must be between -180 and 180"),
});

export const ProfileIdentitySettingsSchema = z
	.object({
		timezone: modeWithValue(
			["auto", "real", "custom"],
			"custom",
			TimeZoneSchema,
			{
				mode: "Timezone must be based on IP, the real system zone, or a custom zone",
				value: "Choose a timezone",
			},
		),
		locale: modeWithValue(["auto", "custom"], "custom", LocaleTagSchema, {
			mode: "Locale must be based on IP or a custom locale",
			value: "Choose a locale",
		}),
		languages: modeWithValue(
			["auto", "custom"],
			"custom",
			z
				.array(LocaleTagSchema, { error: "Languages must be a list" })
				.min(1, "Add at least one language")
				.max(10, "Use at most 10 languages")
				.refine(
					(tags) =>
						new Set(tags.map((tag) => tag.toLowerCase())).size === tags.length,
					"Each language can appear only once",
				),
			{
				mode: "Languages must be based on IP or a custom list",
				value: "Add at least one language",
			},
		),
		location: modeWithValue(
			["auto", "real", "custom"],
			"custom",
			LocationSchema,
			{
				mode: "Location must be based on IP, the real location, or custom coordinates",
				value: "Enter a latitude and longitude",
			},
		),
		browserBrand: BrowserBrandSchema,
		brandFullVersion: versionSchema("Brand version", "142.0.3595.65"),
		uaFullVersion: versionSchema("User agent version", "142.0.7444.60"),
		userAgent: UserAgentSchema,
		platformVersion: versionSchema("Platform version", "13 or 15.0.0"),
		architecture: enumSchema(["x86", "arm", "arm64"], "Architecture"),
		bitness: enumSchema(["32", "64"], "Bitness"),
		mobile: booleanSchema("Mobile"),
	})
	.partial();

// ============ Display ============

export const DimensionsSchema = z.object({
	width: intSchema("Width", 100, 7680),
	height: intSchema("Height", 100, 4320),
});

export const ORIENTATIONS = [
	"profile",
	"landscape",
	"portrait",
	"landscape-primary",
	"landscape-secondary",
	"portrait-primary",
	"portrait-secondary",
] as const;

export const ProfileDisplaySettingsSchema = z
	.object({
		window: modeWithValue(
			["profile", "real", "custom"],
			"custom",
			DimensionsSchema,
			{
				mode: "Window must use the profile, the real window, or a custom size",
				value: "Enter a window width and height",
			},
		),
		screen: modeWithValue(
			["profile", "real", "custom"],
			"custom",
			DimensionsSchema,
			{
				mode: "Screen must use the profile, the real screen, or a custom size",
				value: "Enter a screen width and height",
			},
		),
		dpr: enumSchema(["profile", "real", "advanced"], "Device pixel ratio"),
		orientation: enumSchema(ORIENTATIONS, "Orientation"),
		colorScheme: enumSchema(["light", "dark"], "Color scheme"),
		fonts: enumSchema(["profile", "expand", "real"], "Fonts"),
		keyboard: enumSchema(["profile", "real"], "Keyboard layout"),
		mobileForceTouch: booleanSchema("Force touch"),
		mobileKeyboard: booleanSchema("Mobile keyboard"),
		disableDeviceScaleFactor: booleanSchema("Disable device scale factor"),
	})
	.partial()
	.superRefine((data, ctx) => {
		const window =
			data.window?.mode === "custom" ? data.window.value : undefined;
		const screen =
			data.screen?.mode === "custom" ? data.screen.value : undefined;
		if (!window || !screen) return;
		if (window.width > screen.width) {
			ctx.addIssue({
				code: "custom",
				path: ["window", "value", "width"],
				message: "Window width cannot exceed the screen width",
			});
		}
		if (window.height > screen.height) {
			ctx.addIssue({
				code: "custom",
				path: ["window", "value", "height"],
				message: "Window height cannot exceed the screen height",
			});
		}
	});

// ============ Rendering ============

const NetworkSourceSchema = z.enum(["profile", "host"]);

function networkField<T extends z.ZodType>(value: T, label: string) {
	return z.union([value, NetworkSourceSchema], {
		error: `${label} must be a value, "profile", or "host"`,
	});
}

/** A custom `--bot-network-info-override` policy; omitted fields stay native. */
export const NetworkInfoPolicySchema = z
	.object({
		type: networkField(
			z.enum([
				"bluetooth",
				"cellular",
				"ethernet",
				"mixed",
				"none",
				"other",
				"unknown",
				"wifi",
				"wimax",
			]),
			"Connection type",
		),
		effectiveType: networkField(
			z.enum(["slow-2g", "2g", "3g", "4g"]),
			"Effective type",
		),
		rtt: networkField(
			intSchema("Round-trip time", 0, 60_000),
			"Round-trip time",
		),
		downlink: networkField(
			z.number().min(0, "Downlink cannot be negative").max(10_000),
			"Downlink",
		),
		downlinkMax: networkField(
			z.number().min(0, "Max downlink cannot be negative").max(10_000),
			"Max downlink",
		),
		saveData: networkField(z.boolean(), "Save data"),
	})
	.partial()
	.refine(
		(policy) => Object.values(policy).some((v) => v !== undefined),
		"Set at least one network property",
	);

const BYTES_MIN = 1024 * 1024;

function byteLimitSchema(label: string) {
	return z
		.number({ error: `${label} must be a number of bytes` })
		.int(`${label} must be a whole number of bytes`)
		.min(BYTES_MIN, `${label} must be at least 1 MiB (${BYTES_MIN} bytes)`)
		.max(Number.MAX_SAFE_INTEGER, `${label} is too large`);
}

export const ProfileRenderingSettingsSchema = z
	.object({
		webgl: enumSchema(["profile", "real", "disabled"], "WebGL"),
		webgpu: enumSchema(["profile", "real", "disabled"], "WebGPU"),
		noise: z
			.object({
				canvas: booleanSchema("Canvas noise"),
				webglImage: booleanSchema("WebGL image noise"),
				audioContext: booleanSchema("Audio noise"),
				clientRects: booleanSchema("Client rects noise"),
				textRects: booleanSchema("Text rects noise"),
			})
			.partial(),
		noiseSeed: SeedSchema("Noise seed"),
		timeSeed: SeedSchema("Timing seed"),
		stackSeed: SeedSchema("Stack seed"),
		fps: modeWithValue(
			["profile", "real", "custom"],
			"custom",
			intSchema("FPS", 1, 240),
			{
				mode: "Frame rate must use the profile, the real rate, or a custom value",
				value: "Enter a frame rate",
			},
		),
		videoFps: z.object({
			actual: intSchema("Video FPS", 1, 240),
			reported: z
				.union([intSchema("Reported video FPS", 1, 240), z.literal("real")], {
					error: 'Reported video FPS must be a number or "real"',
				})
				.optional(),
		}),
		timeScale: z
			.number({ error: "Time scale must be a number" })
			.gt(0, "Time scale must be greater than 0")
			.lt(1, "Time scale must be below 1"),
		speechVoices: enumSchema(["profile", "real"], "Speech voices"),
		mediaDevices: enumSchema(["profile", "real"], "Media devices"),
		mediaTypes: enumSchema(["expand", "profile", "real"], "Media types"),
		jsHeapSizeLimit: modeWithValue(
			["profile", "real", "custom"],
			"custom",
			byteLimitSchema("JS heap size limit"),
			{
				mode: "JS heap limit must use the profile, the real value, or a byte count",
				value: "Enter the heap size limit in bytes",
			},
		),
		storageQuota: modeWithValue(
			["profile", "real", "custom"],
			"custom",
			byteLimitSchema("Storage quota"),
			{
				mode: "Storage quota must use the profile, the real value, or a byte count",
				value: "Enter the storage quota in bytes",
			},
		),
		networkInfoOverride: modeWithValue(
			["profile", "native", "custom"],
			"custom",
			NetworkInfoPolicySchema,
			{
				mode: "Network info must use the profile, native values, or a custom policy",
				value: "Set at least one network property",
			},
		),
		gpuEmulation: enumSchema(
			["enabled", "disabled", "priority"],
			"GPU emulation",
		),
	})
	.partial();

// ============ Network ============

const IPV4 = z.ipv4();
const IPV6 = z.ipv6();

function isIpv4(value: string): boolean {
	return IPV4.safeParse(value).success;
}

function isIpv6(value: string): boolean {
	return IPV6.safeParse(value).success;
}

function isPort(value: string): boolean {
	const port = Number(value);
	return /^\d{1,5}$/.test(value) && port >= 1 && port <= 65535;
}

/** `--proxy-ip`: one address per family, or `ipv4_none` / `ipv6_none`. */
export function isValidProxyIp(value: string): boolean {
	const parts = value.split(",").map((part) => part.trim());
	if (parts.length < 1 || parts.length > 2) return false;
	const families = parts.map((part) => {
		if (part === "ipv4_none" || isIpv4(part)) return "v4";
		if (part === "ipv6_none" || isIpv6(part)) return "v6";
		return null;
	});
	if (families.includes(null)) return false;
	return new Set(families).size === families.length;
}

/** `--bot-local-dns`: `IP`, `IPv4:port`, or `[IPv6]:port`. */
export function isValidDnsServer(value: string): boolean {
	if (isIpv4(value) || isIpv6(value)) return true;
	const v4 = /^([\d.]+):(\d+)$/.exec(value);
	if (v4) return isIpv4(v4[1] ?? "") && isPort(v4[2] ?? "");
	const v6 = /^\[([^\]]+)\](?::(\d+))?$/.exec(value);
	if (v6) return isIpv6(v6[1] ?? "") && (v6[2] === undefined || isPort(v6[2]));
	return false;
}

/**
 * The browser matches `--proxy-bypass-rgx` with RE2, which has no lookaround
 * or backreferences. A pattern using them compiles here and then silently
 * fails there, so they are rejected up front.
 */
const RE2_UNSUPPORTED = /\(\?<?[=!]|\\[1-9]|\\k</;

export function proxyBypassRegexError(value: string): string | null {
	if (RE2_UNSUPPORTED.test(value)) {
		return "Lookarounds and backreferences are not supported (RE2 syntax)";
	}
	try {
		new RegExp(value);
		return null;
	} catch {
		return "This is not a valid regular expression";
	}
}

const HEADER_NAME_PATTERN = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;

/** Headers the network stack owns; injecting them breaks requests. */
export const RESERVED_HEADER_NAMES = [
	"host",
	"content-length",
	"transfer-encoding",
	"connection",
	"keep-alive",
	"upgrade",
	"te",
	"trailer",
	"proxy-authorization",
	"proxy-connection",
] as const;

const RESERVED_HEADER_SET = new Set<string>(RESERVED_HEADER_NAMES);

const IceServerSchema = z
	.string({ error: "ICE server must be text" })
	.trim()
	.max(256, "ICE server must be 256 characters or less")
	.regex(
		/^(?:stun|stuns|turn|turns):[^\s,]+$/,
		"ICE server must look like stun:host:port or turn:host:port",
	);

export const ProfileNetworkSettingsSchema = z
	.object({
		webrtc: enumSchema(["profile", "real", "disabled"], "WebRTC"),
		webrtcIce: modeWithValue(
			["google", "custom"],
			"custom",
			z
				.array(IceServerSchema, { error: "ICE servers must be a list" })
				.min(1, "Add at least one ICE server")
				.max(10, "Use at most 10 ICE servers"),
			{
				mode: "ICE servers must use the Google preset or a custom list",
				value: "Add at least one ICE server",
			},
		),
		proxyIp: z
			.string({ error: "Proxy IP must be text" })
			.trim()
			.min(1, "Enter the proxy's exit IP")
			.max(100, "Proxy IP is too long")
			.refine(
				isValidProxyIp,
				"Enter an IPv4 and/or IPv6 address, comma separated (ipv4_none / ipv6_none allowed)",
			),
		localDns: modeWithValue(
			["enabled", "disabled", "custom"],
			"custom",
			z
				.string({ error: "DNS server must be text" })
				.trim()
				.refine(
					isValidDnsServer,
					"Enter a DNS server IP, optionally with a port (8.8.8.8 or 127.0.0.1:5353)",
				),
			{
				mode: "Local DNS must be enabled, disabled, or a custom server",
				value: "Enter a DNS server",
			},
		),
		udpProxy: booleanSchema("UDP over proxy"),
		disableQuic: booleanSchema("Disable QUIC"),
		proxyBypassRgx: z
			.string({ error: "Bypass pattern must be text" })
			.min(1, "Enter a pattern or leave it empty")
			.max(
				SETTINGS_PROXY_BYPASS_MAX_CHARS,
				`Bypass pattern must be ${SETTINGS_PROXY_BYPASS_MAX_CHARS} characters or less`,
			)
			.superRefine((value, ctx) => {
				const error = proxyBypassRegexError(value);
				if (error) ctx.addIssue({ code: "custom", message: error });
			}),
		portProtection: booleanSchema("Port protection"),
		ipService: z
			.array(
				httpUrlSchema("IP service URL").refine(
					(url) => !url.includes(","),
					"IP service URL cannot contain a comma",
				),
				{ error: "IP services must be a list" },
			)
			.min(1, "Add at least one IP service URL")
			.max(5, "Use at most 5 IP service URLs"),
		customHeaders: z
			.record(
				z
					.string()
					.min(1, "Header name is required")
					.max(128, "Header name must be 128 characters or less")
					.regex(HEADER_NAME_PATTERN, "Header name has invalid characters"),
				z
					.string({ error: "Header value must be text" })
					.max(1024, "Header value must be 1024 characters or less")
					.refine(
						(value) => !CONTROL_CHARACTERS.test(value),
						"Header value cannot contain line breaks or control characters",
					),
				{ error: "Custom headers must be name/value pairs" },
			)
			.superRefine((headers, ctx) => {
				const names = Object.keys(headers);
				if (names.length > SETTINGS_CUSTOM_HEADERS_MAX) {
					ctx.addIssue({
						code: "custom",
						message: `Use at most ${SETTINGS_CUSTOM_HEADERS_MAX} custom headers`,
					});
				}
				const seen = new Set<string>();
				for (const name of names) {
					const lower = name.toLowerCase();
					if (RESERVED_HEADER_SET.has(lower)) {
						ctx.addIssue({
							code: "custom",
							path: [name],
							message: `${name} is managed by the browser and cannot be overridden`,
						});
					}
					if (seen.has(lower)) {
						ctx.addIssue({
							code: "custom",
							path: [name],
							message: `${name} is listed more than once`,
						});
					}
					seen.add(lower);
				}
			}),
	})
	.partial();

// ============ Session ============

export interface BookmarkUrl {
	type: "url";
	title: string;
	url: string;
}

export interface BookmarkFolder {
	type: "folder";
	title: string;
	children: BookmarkNode[];
}

export type BookmarkNode = BookmarkUrl | BookmarkFolder;

const BookmarkTitleSchema = z
	.string({ error: "Bookmark title must be text" })
	.max(256, "Bookmark title must be 256 characters or less");

export const BookmarkNodeSchema: z.ZodType<BookmarkNode, BookmarkNode> = z.lazy(
	() =>
		z.discriminatedUnion(
			"type",
			[
				z.object({
					type: z.literal("url"),
					title: BookmarkTitleSchema,
					url: httpUrlSchema("Bookmark URL"),
				}),
				z.object({
					type: z.literal("folder"),
					title: BookmarkTitleSchema,
					children: z.array(BookmarkNodeSchema),
				}),
			],
			{ error: 'Bookmark type must be "url" or "folder"' },
		),
);

function bookmarkStats(nodes: BookmarkNode[], depth = 1) {
	let count = 0;
	let maxDepth = nodes.length > 0 ? depth : depth - 1;
	for (const node of nodes) {
		count += 1;
		if (node.type === "folder") {
			const child = bookmarkStats(node.children, depth + 1);
			count += child.count;
			maxDepth = Math.max(maxDepth, child.maxDepth);
		}
	}
	return { count, maxDepth };
}

export const BookmarksSchema = z
	.array(BookmarkNodeSchema, { error: "Bookmarks must be a list" })
	.superRefine((nodes, ctx) => {
		const { count, maxDepth } = bookmarkStats(nodes);
		if (count > SETTINGS_BOOKMARKS_MAX_NODES) {
			ctx.addIssue({
				code: "custom",
				message: `Use at most ${SETTINGS_BOOKMARKS_MAX_NODES} bookmarks and folders`,
			});
		}
		if (maxDepth > SETTINGS_BOOKMARKS_MAX_DEPTH) {
			ctx.addIssue({
				code: "custom",
				message: `Folders can nest at most ${SETTINGS_BOOKMARKS_MAX_DEPTH} levels deep`,
			});
		}
		if (JSON.stringify(nodes).length > SETTINGS_INLINE_JSON_MAX_CHARS) {
			ctx.addIssue({
				code: "custom",
				message: "Bookmarks are too large; remove some entries",
			});
		}
	});

export const ProfileSessionSettingsSchema = z
	.object({
		bookmarks: BookmarksSchema,
		startUrls: z
			.array(httpUrlSchema("Start URL"), { error: "Start URLs must be a list" })
			.max(
				SETTINGS_START_URLS_MAX,
				`Use at most ${SETTINGS_START_URLS_MAX} start URLs`,
			),
		injectRandomHistory: z.union(
			[z.boolean(), intSchema("History entries", 1, 50)],
			{ error: "Random history must be on, off, or a count from 1 to 50" },
		),
		alwaysActive: booleanSchema("Always active"),
		disableDebugger: booleanSchema("Ignore debugger statements"),
		disableConsoleMessage: booleanSchema("Suppress console messages"),
		cdpCoalesce: booleanSchema("CDP mouse coalescing"),
	})
	.partial();

// ============ Settings ============

export const ProfileSettingsSchema = z
	.object({
		identity: ProfileIdentitySettingsSchema,
		display: ProfileDisplaySettingsSchema,
		rendering: ProfileRenderingSettingsSchema,
		network: ProfileNetworkSettingsSchema,
		session: ProfileSessionSettingsSchema,
	})
	.partial()
	.describe(
		"BotBrowser settings layered over the profile. Unset fields keep the profile default.",
	);

export type ProfileSettings = z.infer<typeof ProfileSettingsSchema>;
export type ProfileIdentitySettings = z.infer<
	typeof ProfileIdentitySettingsSchema
>;
export type ProfileDisplaySettings = z.infer<
	typeof ProfileDisplaySettingsSchema
>;
export type ProfileRenderingSettings = z.infer<
	typeof ProfileRenderingSettingsSchema
>;
export type ProfileNetworkSettings = z.infer<
	typeof ProfileNetworkSettingsSchema
>;
export type ProfileSessionSettings = z.infer<
	typeof ProfileSessionSettingsSchema
>;
export type NetworkInfoPolicy = z.infer<typeof NetworkInfoPolicySchema>;

// ============ OS-dependent settings ============

/** Settings BotBrowser honours on Android profiles only. */
export const MOBILE_ONLY_SETTINGS = [
	["display", "orientation"],
	["display", "mobileKeyboard"],
	["display", "mobileForceTouch"],
] as const;

export type MobileOnlySettingPath = (typeof MOBILE_ONLY_SETTINGS)[number];

export function isMobileOnlySetting(path: readonly string[]): boolean {
	return MOBILE_ONLY_SETTINGS.some(
		([group, field]) => path[0] === group && path[1] === field,
	);
}

export interface SettingsIssue {
	/** Relative to the settings object, e.g. `["display", "orientation"]`. */
	path: string[];
	message: string;
}

/** Mobile-only settings that are set while the profile's OS is not Android. */
export function validateSettingsForOs(
	settings: ProfileSettings | null | undefined,
	os: string,
): SettingsIssue[] {
	if (!settings || os === "android") return [];
	const issues: SettingsIssue[] = [];
	for (const [group, field] of MOBILE_ONLY_SETTINGS) {
		const groupValue = settings[group] as Record<string, unknown> | undefined;
		if (groupValue?.[field] !== undefined) {
			issues.push({
				path: [group, field],
				message: "Only applies to Android profiles",
			});
		}
	}
	return issues;
}

/** The same settings with every mobile-only field removed. */
export function stripMobileOnlySettings(
	settings: ProfileSettings,
): ProfileSettings {
	if (!settings.display) return settings;
	const {
		orientation: _orientation,
		mobileKeyboard: _mobileKeyboard,
		mobileForceTouch: _mobileForceTouch,
		...display
	} = settings.display;
	return { ...settings, display };
}

/**
 * Zod refinement for any object carrying `os` and `settings`, reporting
 * mobile-only settings on a non-Android OS under `settings.<group>.<field>`.
 * A partial update without `os` is skipped; the server checks the merge.
 */
export function refineSettingsForOs(
	data: { os?: string | null; settings?: ProfileSettings | null },
	ctx: z.RefinementCtx,
): void {
	if (!data.os) return;
	for (const issue of validateSettingsForOs(data.settings, data.os)) {
		ctx.addIssue({
			code: "custom",
			path: ["settings", ...issue.path],
			message: issue.message,
		});
	}
}

// ============ Helpers ============

function isEmptyValue(value: unknown): boolean {
	if (value === undefined || value === null) return true;
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === "object") return Object.keys(value).length === 0;
	return false;
}

function compactGroup(
	group: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
	if (!group) return undefined;
	const out: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(group)) {
		const compacted =
			key === "noise" && value && typeof value === "object"
				? compactGroup(value as Record<string, unknown>)
				: value;
		if (!isEmptyValue(compacted)) out[key] = compacted;
	}
	return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Drops unset fields, empty lists, and empty groups. Null when nothing is left,
 * which is how an update clears a profile's settings.
 */
export function compactProfileSettings(
	settings: ProfileSettings | null | undefined,
): ProfileSettings | null {
	if (!settings) return null;
	const out: Record<string, unknown> = {};
	for (const [key, group] of Object.entries(settings)) {
		const compacted = compactGroup(group as Record<string, unknown>);
		if (compacted) out[key] = compacted;
	}
	return Object.keys(out).length > 0 ? (out as ProfileSettings) : null;
}

/** A random seed in 1..2^32-1, for `noiseSeed` / `timeSeed` / `stackSeed`. */
export function randomSettingsSeed(): number {
	const [value = 0] = crypto.getRandomValues(new Uint32Array(1));
	return value === 0 ? 1 : value;
}
